import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, TrendingUp, ShoppingBag, Box, Users } from "lucide-react";
import api, { brl, formatApiError, asList } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", tagline: "", description: "", category: "suplementos", price: "", sale_price: "", image: "", ingredients: "", benefits: "", stock: 100, bestseller: false, rating: 4.8 };
const EMPTY_COUPON = { code: "", description: "", discount_type: "percent", value: "", min_order: 0, active: true };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("produtos");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(asList(r.data))).catch(() => setProducts([]));
    api.get("/admin/orders").then((r) => setOrders(asList(r.data))).catch(() => setOrders([]));
    api.get("/admin/stats").then((r) => setStats(r.data && typeof r.data === "object" ? r.data : null)).catch(() => {});
    api.get("/admin/coupons").then((r) => setCoupons(asList(r.data))).catch(() => setCoupons([]));
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (!user || user.role !== "admin") return null;

  const openNew = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({ ...p, price: p.price, sale_price: p.sale_price ?? "", ingredients: (p.ingredients || []).join(", "), benefits: (p.benefits || []).join(", ") });
    setEditing(p.id); setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      sale_price: form.sale_price !== "" && form.sale_price != null ? parseFloat(form.sale_price) : null,
      stock: parseInt(form.stock) || 0,
      rating: parseFloat(form.rating) || 4.8,
      ingredients: form.ingredients ? form.ingredients.split(",").map((s) => s.trim()).filter(Boolean) : [],
      benefits: form.benefits ? form.benefits.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produto atualizado" : "Produto criado");
      setShowForm(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Produto removido"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...couponForm, value: parseFloat(couponForm.value) || 0, min_order: parseFloat(couponForm.min_order) || 0 });
      toast.success("Cupom criado"); setCouponForm(EMPTY_COUPON); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delCoupon = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); toast.success("Cupom removido"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const STAT_CARDS = stats ? [
    { icon: TrendingUp, label: "Receita", value: brl(stats.revenue) },
    { icon: ShoppingBag, label: "Pedidos", value: stats.orders },
    { icon: Box, label: "Produtos", value: stats.products },
    { icon: Users, label: "Clientes", value: stats.users },
  ] : [];

  return (
    <div className="mx-auto max-w-[1280px] px-4 pb-24 pt-10 sm:px-6">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-clay">Painel administrativo</p>
      <h1 className="mt-3 font-serif text-5xl font-light text-forest-deep">Curadoria & Operações</h1>

      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STAT_CARDS.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="border border-borderlight bg-white p-6">
            <s.icon size={20} className="text-clay" />
            <p className="mt-4 font-serif text-3xl text-forest-deep">{s.value}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink/50">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 flex gap-6 border-b border-borderlight">
        {[["produtos", "Produtos"], ["pedidos", "Pedidos"], ["cupons", "Cupons"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`-mb-px border-b-2 pb-3 font-mono text-[11px] uppercase tracking-widest transition-colors ${tab === k ? "border-clay text-clay" : "border-transparent text-ink/50 hover:text-ink"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "produtos" && (
        <div className="mt-8">
          <button onClick={openNew} data-testid="admin-new-product" className="mb-6 inline-flex items-center gap-2 bg-forest-deep px-6 py-3 font-mono text-xs uppercase tracking-widest text-alabaster hover:bg-clay">
            <Plus size={15} /> Novo produto
          </button>
          <div className="overflow-x-auto border border-borderlight bg-white">
            <table className="w-full text-left">
              <thead className="border-b border-borderlight bg-sand-elevated">
                <tr className="font-mono text-[10px] uppercase tracking-widest text-ink/60">
                  <th className="p-4">Produto</th><th className="p-4">Categoria</th><th className="p-4">Preço</th><th className="p-4">Estoque</th><th className="p-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-b border-borderlight last:border-0">
                    <td className="p-4"><div className="flex items-center gap-3"><img src={p.image} alt="" className="h-12 w-10 object-cover" /><span className="font-serif text-lg text-forest-deep">{p.name}</span></div></td>
                    <td className="p-4 font-mono text-xs uppercase tracking-widest text-ink/60">{p.category}</td>
                    <td className="p-4 font-sans text-sm">{brl(p.price)}</td>
                    <td className="p-4 font-sans text-sm">{p.stock}</td>
                    <td className="p-4"><div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-2 text-ink/60 hover:text-clay"><Pencil size={16} /></button>
                      <button onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 text-ink/60 hover:text-clay"><Trash2 size={16} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "pedidos" && (
        <div className="mt-8 space-y-4">
          {orders.length === 0 ? <p className="py-16 text-center font-serif text-2xl text-ink/40">Nenhum pedido ainda.</p> : orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="border border-borderlight bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-clay">{o.order_number}</p>
                  <p className="mt-1 font-sans text-sm text-ink/70">{o.user_email} · {o.shipping_city}</p>
                  <p className="font-sans text-xs text-ink/50">{new Date(o.created_at).toLocaleString("pt-BR")}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-2xl text-forest-deep">{brl(o.total)}</p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-ink/50">{o.items.length} itens · {o.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "cupons" && (
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <form onSubmit={saveCoupon} data-testid="coupon-form" className="rounded-xl border border-borderlight bg-white p-6">
            <h3 className="font-serif text-2xl text-forest-deep">Novo cupom</h3>
            <div className="mt-5 space-y-4">
              <input required placeholder="Código (ex: BEMVINDO10)" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} data-testid="coupon-form-code"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm uppercase outline-none focus:border-clay" />
              <input placeholder="Descrição" value={couponForm.description} onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })} data-testid="coupon-form-description"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              <div className="grid grid-cols-2 gap-4">
                <select value={couponForm.discount_type} onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })} data-testid="coupon-form-type"
                  className="border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay">
                  <option value="percent">Percentual (%)</option>
                  <option value="fixed">Valor fixo (R$)</option>
                </select>
                <input type="number" step="0.01" required placeholder="Valor" value={couponForm.value} onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })} data-testid="coupon-form-value"
                  className="border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              </div>
              <input type="number" step="0.01" placeholder="Pedido mínimo (R$)" value={couponForm.min_order} onChange={(e) => setCouponForm({ ...couponForm, min_order: e.target.value })} data-testid="coupon-form-min"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
            </div>
            <button type="submit" data-testid="coupon-form-save" className="mt-6 w-full bg-forest-deep py-3 font-mono text-xs uppercase tracking-widest text-alabaster hover:bg-clay">Criar cupom</button>
          </form>
          <div className="space-y-3">
            {coupons.length === 0 ? <p className="py-10 text-center font-serif text-xl text-ink/40">Nenhum cupom criado.</p> : coupons.map((c) => (
              <div key={c.id} data-testid={`admin-coupon-${c.code}`} className="flex items-center justify-between rounded-xl border-2 border-dashed border-clay/40 bg-white p-5">
                <div>
                  <p className="font-mono text-lg font-bold text-forest-deep">{c.code}</p>
                  <p className="mt-0.5 font-sans text-xs text-ink/60">{c.description}</p>
                  <p className="mt-1 font-sans text-xs text-clay">{c.discount_type === "percent" ? `${c.value}% OFF` : `${brl(c.value)} OFF`}{c.min_order ? ` · mín. ${brl(c.min_order)}` : ""}</p>
                </div>
                <button onClick={() => delCoupon(c.id)} data-testid={`delete-coupon-${c.code}`} className="p-2 text-ink/60 hover:text-clay"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-forest-deep/60 p-4 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="product-form"
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto border border-borderlight bg-alabaster p-8">
            <h3 className="font-serif text-3xl text-forest-deep">{editing ? "Editar produto" : "Novo produto"}</h3>
            <div className="mt-6 space-y-4">
              {[["name", "Nome"], ["tagline", "Slogan"], ["image", "URL da imagem"]].map(([k, l]) => (
                <input key={k} required={k === "name"} placeholder={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`form-${k}`}
                  className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              ))}
              <textarea placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" rows={3} />
              <div className="grid grid-cols-2 gap-4">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category"
                  className="border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay">
                  <option value="granel">Granel</option>
                  <option value="suplementos">Suplementos</option>
                  <option value="chas">Chás e Ervas</option>
                  <option value="alimentos">Alimentos Naturais</option>
                  <option value="cosmeticos">Cosméticos Naturais</option>
                  <option value="kits">Kits e Presentes</option>
                </select>
                <input type="number" step="0.01" placeholder="Preço" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price"
                  className="border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              </div>
              <input type="number" step="0.01" placeholder="Preço promocional (opcional)" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="form-sale_price"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              <div className="grid grid-cols-2 gap-4">
                <input type="number" placeholder="Estoque" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="form-stock"
                  className="border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
                <input type="number" step="0.1" placeholder="Avaliação" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="form-rating"
                  className="border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              </div>
              <input placeholder="Ingredientes (separados por vírgula)" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} data-testid="form-ingredients"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              <input placeholder="Benefícios (separados por vírgula)" value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} data-testid="form-benefits"
                className="w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay" />
              <label className="flex items-center gap-2 font-sans text-sm">
                <input type="checkbox" checked={form.bestseller} onChange={(e) => setForm({ ...form, bestseller: e.target.checked })} data-testid="form-bestseller" /> Marcar como mais vendido
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="submit" data-testid="form-save" className="flex-1 bg-forest-deep py-3 font-mono text-xs uppercase tracking-widest text-alabaster hover:bg-clay">Salvar</button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-borderlight px-6 py-3 font-mono text-xs uppercase tracking-widest text-ink hover:border-clay">Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
